import { Alert, Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useGetList, useGetMany, useGetOne, useRecordContext } from "react-admin";
import { InventoryOutlined as StorageIcon } from "@mui/icons-material";
import { Client, DocumentRead, DocumentRecordType, Policy, ResourceName, User, Vendor } from "@/types";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { acknowledgementLabel } from "../utils/acknowledgementLabel";
import { SideDrawer } from "@/components/common/SideDrawer";
import { getResourceIcon } from "@/utils/icons";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { formatRecordType } from "../utils/documentUtils";
import { LinkedEntityRow } from "./LinkedEntityRow";

/** Width of the details drawer, in pixels. Narrower than SideDrawer's 500
 *  default — this is a metadata surface, not a working canvas. Exported so
 *  `DocumentShow` can shrink the main content width to match. */
export const DOCUMENT_DETAILS_DRAWER_WIDTH = 360;

interface DocumentDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Right-anchored, default-open sidebar that surfaces document metadata and
 * linked entities alongside the tab content.
 *
 * Replaces the legacy `DocumentOverviewTab`. The sidebar idiom gives
 * metadata proper visual hierarchy — section labels, key/value rows with
 * breathing room, and link-row navigation to linked entities — instead of
 * cramming everything into a tab whose canvas was mostly whitespace.
 *
 * Drawer open/closed state is owned by the parent (`DocumentShow`) and
 * persisted in localStorage so the user's preference survives navigation.
 *
 * Transformation metadata (template, sub-type, extraction method, coverage)
 * lives in `TransformationHeader` on the Mapping tab instead — it's
 * processing-time data, not document-identity data.
 */
export const DocumentDetailsDrawer = ({ open, onClose }: DocumentDetailsDrawerProps) => {
  const documentRecord = useRecordContext<DocumentRead>();

  if (!documentRecord) return null;

  const isStorageOnly = documentRecord.object_metadata?.storage_only === true;
  const isPolicyDocument = documentRecord.record_type === DocumentRecordType.POLICIES;
  // The drawer renders only while the Result tab's context bar does not (see
  // DocumentShow), so its links always apply: a failed, rejected or not-yet-loaded
  // claims document shows Client and Vendor here as before (#7553 AC6; !4974 31128).
  const hasAnyLink = !!documentRecord.client_id_ref || !!documentRecord.vendor_id_ref || isPolicyDocument;
  const acks = documentRecord.review_acknowledgements ?? [];

  return (
    <SideDrawer title="Details" open={open} onClose={onClose} width={DOCUMENT_DETAILS_DRAWER_WIDTH}>
      <Stack spacing={2.5} sx={{ pb: 3 }}>
        {isStorageOnly && (
          <Alert icon={<StorageIcon fontSize="small" />} severity="info" sx={{ fontSize: "0.8125rem", py: 0.5 }}>
            Storage Only — no extraction or processing was performed.
          </Alert>
        )}

        {/* Document metadata section */}
        <DetailGroup>
          <DetailRow label="Record Type" value={formatRecordType(documentRecord.record_type ?? "")} primary />
          {documentRecord.file_format_name && (
            <DetailRow label="Format" value={`${documentRecord.file_format_name} · v${documentRecord.version ?? 1}`} />
          )}
          {/* Uploaded / Processed render with second-level precision — total
              processing time is typically a few seconds, so sub-minute
              granularity is meaningful here. The tooltip would duplicate the
              same info now that time-of-day is inline, so it's dropped. */}
          {documentRecord.uploaded_datetime && (
            <DetailRow
              label="Uploaded"
              value={formatDate(documentRecord.uploaded_datetime, DATE_FORMATS.DISPLAY_LONG_WITH_TIME)}
            />
          )}
          {!isStorageOnly && documentRecord.processed_datetime && (
            <DetailRow
              label="Processed"
              value={formatDate(documentRecord.processed_datetime, DATE_FORMATS.DISPLAY_LONG_WITH_TIME)}
            />
          )}
        </DetailGroup>

        {acks.length > 0 && (
          <>
            <Divider />
            <ReviewRecordGroup acks={acks} />
          </>
        )}

        {hasAnyLink && (
          <>
            <Divider />
            <DetailGroup>
              <SectionLabel>Linked</SectionLabel>
              {documentRecord.client_id_ref && <ClientRow clientId={documentRecord.client_id_ref} />}
              {documentRecord.vendor_id_ref && <VendorRow vendorId={documentRecord.vendor_id_ref} />}
              {isPolicyDocument && <PolicyRow documentId={documentRecord.id} />}
            </DetailGroup>
          </>
        )}
      </Stack>
    </SideDrawer>
  );
};

/**
 * What the reviewer confirmed before this file loaded (#7550 AC9): one ✓ line
 * per acknowledgement with its reviewer-facing label and detail, the reviewer
 * and date beneath. The undo rule lives in the group's hint, not in prose.
 */
const ReviewRecordGroup = ({ acks }: { acks: NonNullable<DocumentRead["review_acknowledgements"]> }) => {
  const userIds = Array.from(new Set(acks.map((a) => a.user_id).filter((u): u is string => !!u)));
  const { data: users } = useGetMany<User>(ResourceName.USERS, { ids: userIds }, { enabled: userIds.length > 0 });
  const nameFor = (userId?: string): string | null => {
    const u = userId ? users?.find((x) => String(x.id) === userId) : undefined;
    return u ? u.full_name || u.preferred_name || u.email : null;
  };
  return (
    <DetailGroup>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <SectionLabel>Review record</SectionLabel>
        <HintIcon hint="Undo requires an admin to delete this document." />
      </Box>
      <Stack spacing={1} data-testid="review-record-group">
        {acks.map((a, i) => {
          const label = acknowledgementLabel(a);
          const detail = a.detail && a.detail !== label ? a.detail : null;
          const who = nameFor(a.user_id);
          const when = a.at ? formatDate(String(a.at), DATE_FORMATS.DISPLAY_LONG) : null;
          const byline = [who, when].filter(Boolean).join(" · ");
          return (
            <Box key={`${a.check}-${i}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Typography
                component="span"
                sx={{ color: "success.main", fontWeight: 700, lineHeight: 1.4 }}
                aria-hidden="true"
              >
                ✓
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">{detail ? `${label} · ${detail}` : label}</Typography>
                {byline && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
                    {byline}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </DetailGroup>
  );
};

// ---------------------------------------------------------------------------
// Linked entity fetchers — each is a thin wrapper around useGetOne / useGetList
// that resolves to a LinkedEntityRow. Kept colocated; not large enough to
// warrant separate files.
// ---------------------------------------------------------------------------

const ClientRow = ({ clientId }: { clientId: string }) => {
  const { data: client, isLoading } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: clientId },
    { enabled: !!clientId }
  );
  if (isLoading) {
    return <LinkedEntityRow icon={getResourceIcon(ResourceName.CLIENTS)} label="" to="" isLoading />;
  }
  if (!client) return null;
  const name = client.client_legal_name || client.client_short_name || "Client";
  return (
    <LinkedEntityRow
      icon={getResourceIcon(ResourceName.CLIENTS)}
      label={name}
      sublabel={client.client_state_headquartered ?? undefined}
      to={`/${ResourceName.CLIENTS}/${client.id}/show`}
    />
  );
};

const VendorRow = ({ vendorId }: { vendorId: string }) => {
  const { data: vendor, isLoading } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: vendorId },
    { enabled: !!vendorId }
  );
  if (isLoading) {
    return <LinkedEntityRow icon={getResourceIcon(ResourceName.VENDORS)} label="" to="" isLoading />;
  }
  if (!vendor) return null;
  // Mirror LinkedVendorCard's type-derivation order. First match wins as the
  // sublabel — keep the row terse; reviewers click through for full details.
  const sublabel = vendor.is_sl_carrier
    ? "Carrier"
    : vendor.is_tpa
      ? "TPA"
      : vendor.is_rx_vendor
        ? "Rx/PBM"
        : undefined;
  return (
    <LinkedEntityRow
      icon={getResourceIcon(ResourceName.VENDORS)}
      label={vendor.company_name}
      sublabel={sublabel}
      to={`/${ResourceName.VENDORS}/${vendor.id}/show`}
    />
  );
};

const PolicyRow = ({ documentId }: { documentId: string }) => {
  // Mirrors LinkedPolicyCard's lookup — policies that reference this document
  // in their source_documents array. Backend filter support is best-effort.
  const { data: policies, isLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      pagination: { page: 1, perPage: 1 },
      filter: { source_documents: documentId },
    },
    { enabled: !!documentId }
  );
  if (isLoading) {
    return <LinkedEntityRow icon={getResourceIcon(ResourceName.POLICIES)} label="" to="" isLoading />;
  }
  const policy = policies?.[0];
  if (!policy) return null;
  return (
    <LinkedEntityRow
      icon={getResourceIcon(ResourceName.POLICIES)}
      label={policy.aegle_id || policy.id}
      to={`/${ResourceName.POLICIES}/${policy.id}/show`}
    />
  );
};

// ---------------------------------------------------------------------------
// Layout primitives — local; not worth extracting until a second consumer
// exists.
// ---------------------------------------------------------------------------

const DetailGroup = ({ children }: { children: React.ReactNode }) => <Stack spacing={1.5}>{children}</Stack>;

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="div"
    variant="caption"
    color="text.secondary"
    sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, fontSize: "0.6875rem" }}
  >
    {children}
  </Typography>
);

interface DetailRowProps {
  label: string;
  value: string;
  /** Render the value at a larger weight — used for the headline fact. */
  primary?: boolean;
  /** Optional tooltip on the value (e.g. precise timestamp on a humanized date). */
  tooltip?: string;
}

const DetailRow = ({ label, value, primary, tooltip }: DetailRowProps) => {
  const valueNode = (
    <Typography variant={primary ? "body1" : "body2"} fontWeight={primary ? 600 : 500}>
      {value}
    </Typography>
  );

  return (
    <Box>
      <Typography
        component="div"
        variant="caption"
        color="text.secondary"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
          fontSize: "0.6875rem",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="top-start">
          <Box component="span" sx={{ display: "inline-block" }}>
            {valueNode}
          </Box>
        </Tooltip>
      ) : (
        valueNode
      )}
    </Box>
  );
};
